/**
 * Type definitions for BRR Component
 * Extracted from monolithic component for better organization and reusability
 */

// View and Status Types
export type ViewType = 'home' | 'search' | 'search-page' | 'dashboard' | 'start-brr' | 'document-validation' | 'form' | 'qp-review' | 'connection-status';
export type BatchStatus = 'draft' | 'in-progress' | 'pending-qp-review' | 'completed' | 'Released' | 'In Progress';
export type LinkBatchType = 'api' | 'bulk' | 'finished';
export type Priority = 'low' | 'medium' | 'high';
export type UserRole = 'admin' | 'supervisor' | 'viewer';
export type ComplianceStatus = 'compliant' | 'non-compliant';
export type FieldSource =
  | 'SAP'
  | 'CoA'
  | 'BOM'
  | 'Override'
  | 'CARA'
  | 'Manual'
  | 'Document'
  | 'QAA Repository (Status Verträge.xlsx)'
  | 'QAA Repository'
  | 'CMC-Q File Server'
  | 'Artworks / KAU'
  | 'KAU/Artworks'
  | 'Artwork Repository'
  | 'CMO Documentation'
  | 'Box CMO Folders'
  | 'SAP MIGO'
  | 'CARA Quality Events'
  | 'CARA Change Control Data'
  | 'CARA Regulatory Dossier'
  | 'BRR Session'
  | 'User Selection'
  | 'Certificate of Analysis'
  | 'Extracted from Documents'
  | `Extracted from Document: ${string}`
  | 'Manual Override'
  | 'Not Found'
  | 'Error';

// Core Entities
export interface Batch {
  id: string;
  product: string;
  status: BatchStatus;
  type?: string;
  site?: string;
  reviewer?: string;
  qp?: string;
  date?: string;
  country?: string;
  completeness?: number;
  priority?: string;
  level?: string;
  transport?: string;
  manufacturer?: string;
  releaseDate?: string;
  inspectionLot?: string;
  inspectionLots?: InspectionLot[];
}

export interface InspectionLot {
  id: string;
  batchId: string;
  status: string;
}

export interface Document {
  id: string;
  name: string;
  type?: string;
  size?: string;
  uploadTime?: string;
  uploadedAt?: string;
  blobName?: string;  // Blob storage name for deletion (e.g., user_name/filename.pdf)
  url?: string;       // Blob storage URL
  documentType?: string; // Detected/selected document type id (e.g., 'coc', 'coa', 'combined')
  documentTypes?: string[]; // Sub-types when documentType is 'combined' (e.g., ['coa', 'coc'])
  wasExtracted?: boolean; // True if document was loaded from Neo4j (already extracted)
  needsUpload?: boolean; // True for cross-batch clone placeholders that need actual file upload
  templateSourceBlobName?: string; // Original blob name from template (for reference)
}

export interface AdditionalEvidenceEntry {
  id: string;
  title: string;
  description: string;
  boxLink: string;
  createdAt: string;
}

export interface AdditionalEvidenceItem {
  id: string;
  type: 'pdf' | 'boxLink';
  title: string;
  description: string;
  createdAt: string;
  // PDF-specific fields (when type === 'pdf')
  fileName?: string;
  fileSize?: string;
  uploadTime?: string;
  boxFileId?: string;
  // Read-only: set only on evidence that predates the move to Box, kept so those BRRs still render
  blobName?: string;
  url?: string;
  // Box link field (when type === 'boxLink')
  boxLink?: string;
}

export interface LinkedBatch extends Batch {
  relationshipType: LinkBatchType;
}

export interface SKU {
  sku: string;
  name: string;
  latestBatch: string;
  batchDate: string;
  type: LinkBatchType;
}

// Field and Validation Types
export interface FieldValue {
  value: string | boolean;
  source: FieldSource;
  override?: boolean;
}

export interface FieldOverride {
  value: string | boolean;
  originalValue: string | boolean;
  validationNotesArray?: ValidationNote[];
  source?: FieldSource;
}

export interface ValidationNote {
  text: string;
  timestamp: string;
}

// Field-specific compliance option types based on SOP QM-0025.AN-01
export type ComplianceOptionType =
  | 'compliant_doesNotComply_na'        // Compliant | does not comply | n/a
  | 'compliant_doesNotCorrespond_na'    // Compliant | does not correspond | n/a
  | 'current_underReview'               // current | under review (QAA status)
  | 'complies_doesNotComply_na'         // Complies | does not comply | n/a
  | 'compliant_doesNotCorrespond'       // Compliant | does not correspond (no n/a)
  | 'complies_doesNotComply'            // Complies | does not comply (no n/a)
  | 'complies_doesNotCorrespond'        // Complies | does not correspond (no n/a)
  | 'closed_open_na'                    // Closed | open | n/a (deviations)
  | 'created_na'                        // created | n/a (additional documents)
  | 'qpAuthorised_complies_doesNotComply'      // Signing QP authorised: Complies | does not comply
  | 'qpAuthorised_complies_doesNotComply_na'   // Signatory QP authorised: Complies | does not comply | n/a
  | 'standard';                         // Default: Compliant | Non-Compliant | n/a

// Compliance options for each type
// Display labels standardised to "Complies / Does not correspond" per SOP QM-0025.AN-01
export const COMPLIANCE_OPTIONS: Record<ComplianceOptionType, Array<{ value: string; label: string }>> = {
  compliant_doesNotComply_na: [
    { value: 'compliant', label: 'Complies' },
    { value: 'does_not_comply', label: 'Does not correspond' },
    { value: 'na', label: 'n/a' },
  ],
  compliant_doesNotCorrespond_na: [
    { value: 'compliant', label: 'Complies' },
    { value: 'does_not_correspond', label: 'Does not correspond' },
    { value: 'na', label: 'n/a' },
  ],
  current_underReview: [
    { value: 'current', label: 'current' },
    { value: 'under_review', label: 'under review' },
  ],
  complies_doesNotComply_na: [
    { value: 'complies', label: 'Complies' },
    { value: 'does_not_comply', label: 'Does not correspond' },
    { value: 'na', label: 'n/a' },
  ],
  compliant_doesNotCorrespond: [
    { value: 'compliant', label: 'Complies' },
    { value: 'does_not_correspond', label: 'Does not correspond' },
  ],
  complies_doesNotComply: [
    { value: 'complies', label: 'Complies' },
    { value: 'does_not_comply', label: 'Does not correspond' },
  ],
  complies_doesNotCorrespond: [
    { value: 'complies', label: 'Complies' },
    { value: 'does_not_correspond', label: 'Does not correspond' },
  ],
  closed_open_na: [
    { value: 'closed', label: 'Closed' },
    { value: 'open', label: 'Open' },
    { value: 'na', label: 'n/a' },
  ],
  created_na: [
    { value: 'created', label: 'Created' },
    { value: 'na', label: 'n/a' },
  ],
  qpAuthorised_complies_doesNotComply: [
    { value: 'qp_authorised_complies', label: 'Signing QP authorised: Complies' },
    { value: 'qp_authorised_does_not_comply', label: 'Signing QP authorised: Does not correspond' },
  ],
  qpAuthorised_complies_doesNotComply_na: [
    { value: 'qp_authorised_complies', label: 'Signatory QP authorised: Complies' },
    { value: 'qp_authorised_does_not_comply', label: 'Signatory QP authorised: Does not correspond' },
    { value: 'na', label: 'n/a' },
  ],
  standard: [
    { value: 'compliant', label: 'Complies' },
    { value: 'non_compliant', label: 'Does not correspond' },
    { value: 'na', label: 'n/a' },
  ],
};

// Map field IDs to their compliance option type (based on SOP QM-0025.AN-01)
export const FIELD_COMPLIANCE_TYPE: Record<string, ComplianceOptionType> = {
  // Supplier Status section
  cmoName: 'compliant_doesNotComply_na',
  cmoQualStatus: 'compliant_doesNotComply_na',
  cmoComplianceReviewDate: 'compliant_doesNotComply_na',
  qaaStatus: 'current_underReview',
  qaaValidity: 'current_underReview',

  // Approval Details section
  marketAuthorisationNumber: 'compliant_doesNotCorrespond_na',
  authorisationStatus: 'compliant_doesNotCorrespond_na',
  validityPeriodEndValue: 'compliant_doesNotCorrespond_na',
  apiName: 'compliant_doesNotComply_na',
  apiManufacturer: 'compliant_doesNotComply_na',
  reportedShelfLife: 'compliant_doesNotComply_na',
  primaryPackaging: 'compliant_doesNotComply_na',
  bulkManufacturer: 'compliant_doesNotComply_na',
  qcTestingSite: 'compliant_doesNotComply_na',
  primaryPackagingSite: 'compliant_doesNotComply_na',
  secondaryPackagingSite: 'compliant_doesNotComply_na',
  releaseSite: 'compliant_doesNotComply_na',

  // Storage Information section
  targetStorageLocation: 'complies_doesNotCorrespond',
  storageStatement: 'complies_doesNotCorrespond',
  countryValidationStatus: 'compliant_doesNotCorrespond_na',

  // Quality Management section
  changeControls: 'standard',
  deviations: 'closed_open_na',

  // Technical Documentation - sample documents (legacy keys)
  specs: 'complies_doesNotComply',
  manufacturing: 'complies_doesNotComply',
  release: 'complies_doesNotCorrespond',
  // Technical Documentation - current keys (match STANDARD_DOC_SECTIONS)
  specsApi: 'complies_doesNotComply',
  primaryPackagingMaterials: 'complies_doesNotComply',
  manufacturingInstructions: 'complies_doesNotComply',
  packagingInstructions: 'complies_doesNotComply',
  releaseTestInstructions: 'complies_doesNotCorrespond',
  releaseSpecifications: 'complies_doesNotCorrespond',

  // Batch Documents
  cpInternalDocuments: 'complies_doesNotComply_na',
  packagingMaterialsUsed: 'compliant_doesNotCorrespond_na',
  term: 'compliant_doesNotCorrespond',
  batchManufactureDate: 'standard',
  batchExpiryDate: 'standard',
  materialsUsed: 'standard',
  oos: 'closed_open_na',

  // Certificates
  coa: 'compliant_doesNotCorrespond',
  cocBatchConfirmation: 'qpAuthorised_complies_doesNotComply',
  cocBatchCertification: 'qpAuthorised_complies_doesNotComply_na',

  // Serialization & Aggregation
  serializationRequirement: 'standard',
  tracelinkGtin: 'standard',
  tracelinkQuantity: 'standard',
  aggregationRequirement: 'standard',

  // Additional documents
  additionalDocuments: 'created_na',
};

// Helper to get compliance options for a field
export function getComplianceOptionsForField(fieldId: string): Array<{ value: string; label: string }> {
  const optionType = FIELD_COMPLIANCE_TYPE[fieldId] || 'standard';
  return COMPLIANCE_OPTIONS[optionType];
}

// Helper to get display label for a compliance value
export function getComplianceDisplayLabel(fieldId: string, value: string | undefined): string {
  if (!value) return '';
  const options = getComplianceOptionsForField(fieldId);
  const option = options.find(o => o.value === value);
  return option?.label || value;
}

// Changed from boolean to string to support field-specific options
export type ComplianceOverrides = Record<string, string>;
export type FieldOverrides = Record<string, FieldOverride>;
export type CheckedDocuments = Record<string, boolean>;

// BRR Form Data Structure
export interface BRRFormData {
  // SAP Identity Batch
  sapIdentityBatch?: SapIdentityBatchData;

  // CMO Information
  cmoInformation?: {
    cmoName?: FieldValue;
    contactEmail?: FieldValue;
    contactPhone?: FieldValue;
    address?: FieldValue;
    [key: string]: FieldValue | undefined;
  };

  // Batch Details
  batchDetails?: {
    batchNumber?: FieldValue;
    productSKU?: FieldValue;
    productName?: FieldValue;
    quantity?: FieldValue;
    manufactureDate?: FieldValue;
    expiryDate?: FieldValue;
    [key: string]: FieldValue | undefined;
  };

  // Packaging Materials (legacy field structure - deprecated)
  packagingMaterialsLegacy?: {
    primaryPackaging?: FieldValue;
    secondaryPackaging?: FieldValue;
    labelingDetails?: FieldValue;
    [key: string]: FieldValue | undefined;
  };

  // Deviations and OOS
  deviationsOos?: {
    deviations?: {
      id: string;
      category: string;
      status: string;
      severity: string;
      marketScope: string;
      description: string;
    }[];
    oosEvents?: {
      id: string;
      parameter: string;
      result: string;
      specification: string;
      status: string;
    }[];
  };

  // Product Registration
  productRegistration?: {
    apiName?: FieldValue;
    apiManufacturer?: FieldValue[];
    registrationNumber?: FieldValue;
    [key: string]: FieldValue | FieldValue[] | undefined;
  };

  // Other sections as needed
  [key: string]: any;
}

// API Request/Response Types
export interface BRRData {
  batchId: string;
  status: 'draft' | 'submitted' | 'approved' | 'rejected';
  formData: BRRFormData;
  lastModifiedBy: string;
  lastModifiedAt: string;
  submittedBy?: string;
  submittedAt?: string;
  approvedBy?: string;
  approvedAt?: string;
}

export interface BatchSummary {
  batchId: string;
  productName: string;
  cmoName: string;
  status: BatchStatus;
  lastSaved?: string;
  completeness?: number;
}

export interface DocumentUploadResponse {
  status: 'success' | 'partial' | 'failed';
  user_name: string;
  uploaded_count: number;
  failed_count: number;
  uploaded: Array<{ filename: string; blob_name: string; url: string }>;
  errors: Array<{ filename: string; error: string }>;
}

// Session/Audit record types
/**
 * BRRSession record as returned by the backend (snake_case keys).
 * ALWAYS access properties via sessionClient.columns rather than hardcoding keys:
 * @example
 * // ✓ Correct - resolves the backend key via the column map
 * const batchId = session[sessionClient.columns.batchid];
 * const status = session[sessionClient.columns.status];
 */
export interface BRRSession {
  [key: string]: any;
}

/**
 * BRRAuditLog record as returned by the backend (snake_case keys).
 * ALWAYS access properties via sessionClient.columns rather than hardcoding keys:
 * @example
 * // ✓ Correct - resolves the backend key via the column map
 * const action = log[sessionClient.columns.action];
 * const timestamp = log[sessionClient.columns.timestamp];
 */
export interface BRRAuditLog {
  [key: string]: any;
}

// Packaged Medicinal Product types (Level 2 PMP sub-dropdown)
export interface PackagedProductMarket {
  countryCode: string;
  countryName: string;
}

export interface PackagedProduct {
  id: string;
  name: string;
  markets: PackagedProductMarket[];
}

export interface PackagedProductsResponse {
  products: PackagedProduct[];
}

// Product match and comparison types
export interface ProductMarket {
  name: string | null;
  isoCode: string | null;
}

export interface ProductShelfLife {
  period: string | null;
  storageConditions: string | null;
}

export interface EnrichedProduct {
  name: string;
  uid: string | null;
  doseForm: string | null;
  doseStrength: string | null;
  packQuantity: string | null;
  pharmaceuticalProduct: string | null;
  medicinalProduct: string | null;
  globalProduct: string | null;
  markets: ProductMarket[];
  shelfLives: ProductShelfLife[];
  matchConfidence: number | null;
  matchReasoning: string | null;
  matchRank: number | null;
  matchSource: string | null;
  confirmedBy: string | null;
  confirmedDate: string | null;
  userSelected: boolean | null;
  productType: string | null;
}

export interface SAPMaterial {
  productName: string | null;
  nameOnPackaging: string | null;
  nameTargetCountry: string | null;
  dosageForm: string | null;
  strength: string | null;
  packageSize: string | null;
  temperatureConditions: string | null;
  destinationCountry: string | null;
  countryCodes: string | null;
}

// Inspection Lot Info Response
export type ProductType = 'pharmaceutical' | 'pmp';

export interface ArtworkInfo {
  materialId: string | null;
  version: string | null;
  cmoMaterialNumber: string | null;
  materialGroupLabel: string | null;
}

export interface InspectionLotInfoResponse {
  batchId: string | null;
  materialId: string | null;
  vendorBatchId: string | null;
  matchType: 'exact' | 'confirmed' | 'candidates' | null;
  products: EnrichedProduct[];
  productType: ProductType;
  sapMaterial: SAPMaterial | null;
  country: string | null;
  materialDescription: string | null;
  artworks: ArtworkInfo[];
}

export interface PackagedProductMarket {
  countryCode: string;
  countryName: string;
}

export interface PackagedProduct {
  id: string;
  name: string;
  markets: PackagedProductMarket[];
}

export interface PackagedProductsResponse {
  products: PackagedProduct[];
}

// Lot Release Dates Response (Ticket #418, enriched in #667)
export interface LotReleaseInfo {
  usageDecisionDatetime: string | null;
  usageDecisionCode: string | null;
  usageDecisionText: string | null;
  releaseStatus: 'released' | 'rejected' | 'pending';
}

export interface LotReleaseDatesResponse {
  releaseDates: Record<string, LotReleaseInfo | null>;
}

// BRR Orchestrator Request/Response Types
export interface BRRProcessRequest {
  product_name: string;       // Required: UI-selected product name for Box folder
  batch_id?: string;          // Optional: batch ID appended to product folder name
  inspection_lot?: string;    // Optional: inspection lot number for traceability
  session_id?: string;        // Optional: session ID for isolation (creates subfolder)
  blob_prefix?: string;       // Optional: prefix/folder in blob storage (empty = root)
  container?: string;         // Optional: source container name (default: brr-documents)
  results_container?: string; // Optional: results container name (default: brr-results)
  document_types?: Record<string, string | string[]>; // blobName → backend doc type(s)
  existing_document_counts?: Record<string, number>; // Existing counts per doc type (for resuming drafts)
  already_extracted_blobs?: string[]; // Blob names to skip (already extracted in saved draft)
}

export interface BRRProcessResponse {
  id: string;                 // Orchestration instance ID
  statusQueryGetUri: string;  // URL for polling status
  sendEventPostUri: string;   // URL for sending events to orchestration
  terminatePostUri: string;   // URL for terminating orchestration
  purgeHistoryDeleteUri: string; // URL for purging orchestration history
}

export interface BoxArchiveInfo {
  status: 'success' | 'partial' | 'failed' | 'skipped';
  folder: string | null;
  folder_id: string | null;
  folder_url?: string | null;
  uploaded_count: number;
  failed_count: number;
  errors: Array<{ blob_name: string; error: string }>;
}

export interface OrchestratorCustomStatus {
  phase: string;
  progress: number;
  message: string;
  total_documents: number;
  documents_processed: number;
  documents_failed: number;
}

export interface OrchestratorStatus {
  instanceId: string;
  runtimeStatus: "Running" | "Completed" | "Failed" | "Terminated" | "Pending";
  output: {
    status: "completed" | "error";
    documents_processed: number;
    documents_failed: number;
    box_archive?: BoxArchiveInfo;
    message?: string;         // Error message if status is "error"
  } | null;
  customStatus?: OrchestratorCustomStatus | null;
  createdTime: string;
  lastUpdatedTime: string;
}

// Validation Page Types
export interface ValidationSource {
  value: string | null;
  source: string;
}

export interface FieldHighlightRect {
  x0: number;
  y0: number;
  x1: number;
  y1: number;
}

export interface FieldHighlight {
  page: number;
  rects: FieldHighlightRect[];
  matched_text: string;
  match_type: 'exact' | 'fuzzy' | 'llm_vision';
}

export interface ValidationTableRow {
  field_name: string;
  extraction_key: string;
  extracted_value: string | null;
  original_value: string | null;
  validation_source_1: ValidationSource | null;
  validation_source_2: ValidationSource | null;
  validation_source_3: ValidationSource | null;
  match_status: 'match' | 'mismatch' | 'missing';
  highlight?: FieldHighlight[];
  ocr_confidence?: number;
}

export interface ValidationTableData {
  title: string;
  filename: string | null;
  blob_name?: string | null;
  source_file?: string | null;
  rows: ValidationTableRow[];
}

/** Full response — table values are null when that document type was not processed */
export interface ValidationPageData {
  status: string;
  documents_found: number;
  tables: Record<string, ValidationTableData | null>;
  materialNumber?: string | null;
  vendorBatchId?: string | null;
  missing_required_documents?: string[];
}

export interface TraceLinkLotStatusResponse {
  found: boolean;
  message?: string;
  lotStatus?: Record<string, string>;
}

export interface TraceLinkHierarchyLevel {
  level: string;
  quantity: number;
}

export interface TraceLinkHierarchyLevelsResponse {
  found: boolean;
  message?: string;
  levels?: TraceLinkHierarchyLevel[];
}

// BRR Form Data API Response
export interface BRRFormFieldValue {
  value: string | boolean;
  source: FieldSource;
}

export interface BRRFormFieldStringValue {
  value: string;
  source: FieldSource;
}

export interface BRRFormDataQualityEvent {
  name: string | null;
  description: string | null;
  titleValue: string | null;
  affectedProducts: string | string[] | null;
  affectedBatchNumber: string | null;
  finalApprovalDate: string | null;
  finalApprovalCompleted: boolean | null;
  targetCompletionDate: string | null;
  matchedVia: string | string[] | null;
  affectedMarket: string | string[] | null;
  eventType: string | null;
}

export interface BRRFormDataAction {
  name: string | null;
  titleValue: string | null;
  description: string | null;
  status: string | null;
  dueDate: string | null;
  initiationDate: string | null;
  completionDate: string | null;
}

export interface BRRFormDataChangeControl {
  name: string | null;
  description: string | null;
  titleValue: string | null;
  status: string | null;
  regulatoryRelevance: string | null;
  finalApprovalDate: string | null;
  affectedProducts: string | null;
  matchedVia: string | string[] | null;
  affectedMarket: string | string[] | null;
  actions: BRRFormDataAction[];
}

export interface BRRFormDataSupplier {
  name: string | null;
  supplierId: string | null;
  validToDate: string | null;
}

export interface BRRFormDataPO {
  goodsMovementQuantity: string | null;
}

export interface BRRFormDataSupplierWithDate {
  name: BRRFormFieldStringValue;
  validToDate: BRRFormFieldStringValue;
}

/** Per-lot SAP quantity data (for multi-lot BRRs) */
export interface SapLotQuantity {
  lotId: string;
  quantity: number | null;
}

/** SAP Identity Batch section data structure */
export interface SapIdentityBatchData {
  sapInternalBatch?: FieldValue;
  cmoExternalBatchSAP?: FieldValue;
  cmoFpBatchId?: FieldValue;
  cmoBulkBatchId?: FieldValue;
  inspectionLotNumber?: FieldValue;
  poNumber?: FieldValue;
  poLineItem?: FieldValue;
  batchQuantity?: FieldValue;
  batchQuantitySAP?: FieldValue;
  unitOfMeasure?: FieldValue;
  lotStatus?: FieldValue;
  lotPlant?: FieldValue;
  batchManufactureDate?: FieldValue;
  batchExpiryDate?: FieldValue;
  batchManufactureDateSAP?: FieldValue;
  batchExpiryDateSAP?: FieldValue;
  calculatedExpiryDate?: FieldValue;
  calculatedExpiryFormula?: FieldValue;
  calculatedExpiryMatch?: FieldValue;
  calculatedExpiryShelfLifeSource?: FieldValue;
  targetStorageLocation?: FieldValue;
  deliveryType?: FieldValue;
  sapLotQuantities?: SapLotQuantity[] | null;
}

export interface BRRFormDataResponse {
  sapIdentityBatch: {
    sapInternalBatch: BRRFormFieldValue;
    cmoExternalBatchSAP: BRRFormFieldValue;
    cmoFpBatchId: BRRFormFieldValue;
    cmoBulkBatchId: BRRFormFieldValue;
    inspectionLotNumber: BRRFormFieldValue;
    poNumber: BRRFormFieldValue;
    poLineItem: BRRFormFieldValue;
    batchQuantity: BRRFormFieldValue;
    batchQuantitySAP: BRRFormFieldValue;
    unitOfMeasure: BRRFormFieldValue;
    lotStatus: BRRFormFieldValue;
    lotPlant: BRRFormFieldValue;
    batchManufactureDate: BRRFormFieldValue;
    batchExpiryDate: BRRFormFieldValue;
    batchManufactureDateSAP: BRRFormFieldValue;
    batchExpiryDateSAP: BRRFormFieldValue;
    calculatedExpiryDate: BRRFormFieldValue;
    calculatedExpiryFormula: BRRFormFieldValue;
    calculatedExpiryMatch: BRRFormFieldValue;
    calculatedExpiryShelfLifeSource: BRRFormFieldValue;
    targetStorageLocation?: BRRFormFieldValue;
    /** Per-lot SAP quantities (only populated when multiple lots selected) */
    sapLotQuantities?: SapLotQuantity[] | null;
  };
  productMarketData: {
    productNameGlobal: BRRFormFieldValue;
    productName: BRRFormFieldValue;
    productNameCARA: BRRFormFieldValue;
    packSize: BRRFormFieldValue;
    packSizeSAP: BRRFormFieldValue;
    packSizeCARA: BRRFormFieldValue;
    dosageForm: BRRFormFieldValue;
    dosageFormSAP: BRRFormFieldValue;
    dosageFormCARA: BRRFormFieldValue;
    materialSKU: BRRFormFieldValue;
    materialSKUSAP: BRRFormFieldValue;
    doseStrength: BRRFormFieldValue;
    doseStrengthSAP: BRRFormFieldValue;
    doseStrengthCARA: BRRFormFieldValue;
    targetCountry: BRRFormFieldValue;
    targetCountrySAP: BRRFormFieldValue;
    shelfLife: BRRFormFieldValue;
    storageStatement: BRRFormFieldValue;
    totalShelfLife: BRRFormFieldValue;
    shelfLifeInMonths: BRRFormFieldValue;
    temperatureConditions: BRRFormFieldValue;
    countryCodes: BRRFormFieldValue;
    productNameOnPackaging: BRRFormFieldValue;
    packQuantity: BRRFormFieldValue;
    marketCountryName: BRRFormFieldValue;
  };
  cmoSupplierQual: {
    cmoName: BRRFormFieldValue;
    cmoNameOptions: BRRFormFieldStringValue[];
    cmoNameMatch: {
      supplierName: string;
      matchedName: string | null;
      score: number | null;
    };
    cmoNameSAPList: BRRFormDataSupplierWithDate[];
  };
  approvalDetails: {
    marketAuthorisationNumber: BRRFormFieldStringValue[];
    marketAuthorisationNumberCARA: BRRFormFieldValue;
    packageLevelValue: BRRFormFieldValue;
    authorisationStatus: BRRFormFieldValue;
    validityPeriodEndValue: BRRFormFieldValue;
    apiName: BRRFormFieldStringValue[];
    apiManufacturer: BRRFormFieldStringValue[];
    bulkManufacturer: BRRFormFieldStringValue[];
    qcTestingSite: BRRFormFieldStringValue[];
    primaryPackagingSite: BRRFormFieldStringValue[];
    secondaryPackagingSite: BRRFormFieldStringValue[];
    releaseSite: BRRFormFieldStringValue[];
    reportedShelfLife: BRRFormFieldValue;
  };
  qualityEvents: {
    deviations: BRRFormDataQualityEvent[];
    outOfSpecifications: BRRFormDataQualityEvent[];
    complaints: BRRFormDataQualityEvent[];
    marketComplaints: BRRFormDataQualityEvent[];
    outOfTrend: BRRFormDataQualityEvent[];
  };
  changeControls: BRRFormDataChangeControl[];
  supplier: BRRFormDataSupplier | null;
  po: BRRFormDataPO | null;
  boxArchive?: BoxArchiveInfo;
  materialBoxFolders?: MaterialBoxFolders | null;
  materialsUsed?: MaterialUsedEntry[];
}

export interface MaterialUsedEntry {
  id?: string;
  material: string;
  vendorBatchId: string;
  source?: 'extracted' | 'manual';
}

export const MATERIAL_TYPE_OPTIONS = [
  { value: 'Finished Product', label: 'Finished Product' },
  { value: 'Packaging', label: 'Packaging' },
  { value: 'API', label: 'API' },
  { value: 'Bulk Material', label: 'Bulk Material' },
  { value: 'Semi-Finished Product', label: 'Semi-Finished Product' },
  { value: 'Sample', label: 'Sample' },
  { value: 'Reference Product', label: 'Reference Product' },
  { value: 'Artwork Batch', label: 'Artwork Batch' },
  { value: 'Medical Equipment', label: 'Medical Equipment' },
  { value: 'Other', label: 'Other' },
] as const;

export type BRRLevel = 'level1' | 'level2' | 'na';

export type DelayReason = 'no_inspection_lot' | 'missing_cmo_documents' | 'missing_serialisation' | 'other';

export const DELAY_REASON_LABELS: Record<string, string> = {
  no_inspection_lot: 'No Inspection Lot',
  missing_cmo_documents: 'Missing CMO Docs',
  missing_serialisation: 'Missing Serialisation',
  other: 'Other',
};

// Document counts for multi-instance document types
// Keys are document type IDs from StartBRRPage (e.g., 'packaging', 'bulkCoa', 'delivery')
export type DocumentCounts = Record<string, number>;

// BRR Review Save Types - Nested Structure

// Page-specific data types
export interface StartPageData {
  searchMethod?: 'inspection-lot' | 'batch-id' | 'material-id' | 'vendor-batch-id';
  inspectionLotId?: string | null;
  selectedLotIds?: string[];
  batchIdSearchText?: string;
  fieldSearchText?: string;
  materialSearchText?: string;
  selectedMaterialId?: string | null;
  materialId?: string | null;
  selectedVendorBatchId?: string | null;
  vendorBatchId?: string | null;
  productName?: string;
  productUid?: string | null;
  destinationCountry?: string;
  materialDescription?: string;
  indirectTransportLotId?: string | null;
  uploadedDocuments?: Array<{
    id: string;
    name: string;
    type?: string;
    size?: string;
    uploadTime?: string;
    blobName?: string;
    url?: string;
    documentType?: string;
    documentTypes?: string[];
    wasExtracted?: boolean; // True if document was loaded from Neo4j (already extracted)
  }>;
  confirmationChecked?: boolean;
  brrLevel?: BRRLevel;
  packagedMedicinalProduct?: { id: string; name: string } | null;
  delayReason?: DelayReason | null;
  delayDetails?: string;
  delayActions?: string;
  delayCmoName?: string | null;
  expectedReleaseDate?: string;
}

export interface ManualSectionEntry {
  id: string;
  tableKey: string;
  documentType: string;
  syncStatus?: 'pending' | 'synced' | 'error';
}

export interface DocumentValidationData {
  // Manual edits to extracted values, keyed by tableKey -> extractionKey -> value
  overrides?: Record<string, Record<string, string>>;
  // User confirmation that all fields have been reviewed
  fieldsConfirmed?: boolean;
  // Serialization requirement dropdown selection
  serializationRequirement?: string;
  // Aggregation requirement dropdown selection
  aggregationRequirement?: string;
  // ISO timestamp when validation was completed
  validatedAt?: string;
  // Manually-added document sections
  manualSections?: ManualSectionEntry[];
  // Cached synced validation results for manual sections
  syncedResults?: Record<string, any>;
  // Full validation tables snapshot at save time (enables loading without blob storage)
  tablesSnapshot?: ValidationPageData;
  // Table keys of documents that have been deleted by the user
  deletedDocuments?: string[];
  // TraceLink lot status data (saved for Word export)
  tracelinkGtin?: string;
  tracelinkQuantity?: string;
  // TraceLink serial number hierarchy level quantities
  tracelinkHierarchyLevels?: TraceLinkHierarchyLevel[];
}

export interface ManualChangeControl {
  stableId: string;
  actionId: string;
  text: string;
  status: string;
  source: string;
  actions: BRRFormDataAction[];
}

export interface ManualQualityEvent {
  stableId: string;
  id: string;
  eventType: string;
  description: string;
  affectedProducts: string;
  affectedBatchNumber: string;
  finalApprovalDate: string;
  finalApprovalCompleted: boolean | null;
  targetCompletionDate: string;
  matchedVia: string;
}

export interface PackagingMaterialEntry {
  id: string;
  componentType: string;  // e.g., "Folding Box", "Leaflet", "Foil", "Label"
  cpMaterialNumber: string;
  cmoMaterialNumber: string;
  versionNumber: string;
  source: 'extracted' | 'manual';
  filename?: string;  // Source document filename (for extracted entries)
}

export interface BRRFormData {
  brrLevel?: 'level1' | 'level2' | 'na';
  brrCreationBasis?: string[];
  complianceOverrides?: ComplianceOverrides;
  fieldOverrides?: FieldOverrides;
  autoPopulatedData?: Record<string, unknown>;
  countrySpecificRequirements?: string;
  manualSAPEntry?: boolean;
  nextPeriodicReview?: string;
  changeControlNotes?: Array<{ text: string; timestamp: string }>;
  individualCCNotes?: Record<string, Array<{ text: string; timestamp: string }>>;
  boxFolderUrls?: Record<string, BoxSubsectionData>;
  manuallyAddedCCs?: ManualChangeControl[];
  manuallyAddedQEs?: ManualQualityEvent[];
  removedCCIds?: string[];
  removedQEIds?: string[];
  documentSectionData?: Record<string, { version: string; freeText: string }>;
  customDocumentSections?: CustomDocumentSection[];
  cpInternalDocs?: string[];
  packagingMaterials?: PackagingMaterialEntry[];  // KAU data (extracted + manual)
  batchReleaseSite?: string;  // GMP Annex 16 batch release site notes
  batchReleaseSiteConfirmed?: boolean;  // GMP Annex 16 batch release site confirmation checkbox (by manufacturer)
  batchReleaseSiteCheplapharmConfirmed?: boolean;  // GMP Annex 16 certification of batch by Cheplapharm
  selectedReleaseSites?: string[];  // GMP Annex 16 user-selected release sites
  materialsUsed?: MaterialUsedEntry[];
  savedAt?: string;
  // Serialization & Aggregation
  serializationRequirement?: string;
  aggregationRequirement?: string;
  tracelinkGtin?: string;
  tracelinkQuantity?: string;
  tracelinkHierarchyLevels?: TraceLinkHierarchyLevel[];
}

export const CP_INTERNAL_DOC_ITEMS = [
  { value: 'secondary-packaging', label: 'Secondary packaging documentation (SOP PR-0020)' },
  { value: 'transport-protocol', label: 'Transport protocol (SOP DC-0039.AN-01, incl. HFW MSH)' },
  { value: 'goods-receipt-protocol', label: 'Incoming goods receipt inspection (SOP DC-0070 / VA DC-001.AN-01)' },
  { value: 'transport-report', label: 'Transport Report (VA QM-0037.AN-01)' },
  { value: 'na', label: 'N/A (e.g. direct transport)' },
] as const;

export interface QPReviewData {
  fieldOverrides?: FieldOverrides;
  complianceOverrides?: ComplianceOverrides;
  autoPopulatedData?: Record<string, unknown>;
  additionalEvidenceDocuments?: Array<{
    id: string;
    name: string;
    type?: string;
    size?: string;
    uploadTime?: string;
    blobName?: string;
    url?: string;
  }>;
  qpAddedCCs?: ManualChangeControl[];
  cpInternalDocs?: string[];
  additionalEvidenceEntries?: AdditionalEvidenceEntry[];
  // New unified format (preferred)
  additionalEvidenceItems?: AdditionalEvidenceItem[];
  packagingMaterials?: PackagingMaterialEntry[];  // KAU data (extracted + manual entries)
  materialsUsed?: MaterialUsedEntry[];  // Materials consumed (inherited from BRR Form)
  batchReleaseSite?: string;  // GMP Annex 16 batch release site notes
  batchReleaseSiteConfirmed?: boolean;  // GMP Annex 16 batch release site confirmation checkbox (by manufacturer)
  batchReleaseSiteCheplapharmConfirmed?: boolean;  // GMP Annex 16 certification of batch by Cheplapharm
  selectedReleaseSites?: string[];  // GMP Annex 16 user-selected release sites
  qpName?: string;
  qpEmail?: string;
  savedAt?: string;
  // Serialization & Aggregation (editable by QP, synced back to documentValidation for export)
  serializationRequirement?: string;
  aggregationRequirement?: string;
}

// Complete BRR review with nested page data (used for GET responses)
export interface BRRReviewComplete {
  batchId: number;
  sessionId: string;
  userEmail: string;
  batchType?: 'direct' | 'indirect';
  priority?: Priority;
  priorityLockedBy?: string;
  priorityLockedAt?: string;
  status: 'draft' | 'submitted';
  savedAt?: string; // ISO timestamp
  // Clone audit trail
  clonedFromSessionId?: string;
  clonedAt?: string;
  // Nested page sections
  startPage?: StartPageData;
  documentValidation?: DocumentValidationData;
  brrForm?: BRRFormData;
  qpReview?: QPReviewData;
}

// Lightweight BRR review metadata for listing (no page data)
export interface BRRReviewSummary {
  sessionId: string;
  batchId: string;
  userEmail: string;
  status: 'draft' | 'submitted';
  savedAt?: string;
  batchType?: 'direct' | 'indirect';
  priority?: Priority;
  priorityLockedBy?: string;
  priorityLockedAt?: string;
  productName?: string;
  inspectionLotId?: string;
}

export interface BRRReviewsByBatchResponse {
  batchId: number;
  reviews: BRRReviewSummary[];
  count: number;
}

export interface BRRReviewsByMaterialResponse {
  materialId: string;
  reviews: BRRReviewSummary[];
  count: number;
}

export interface BRRCloneRequest {
  sourceBatchId: string;
  sourceSessionId: string;
  newSessionId: string;
  targetBatchId?: string;  // For cross-batch template clone
  targetSelectedLotIds?: string[];  // Lot IDs for target batch
}

export interface BRRCloneResponse {
  batchId: number;  // Target batch ID (may differ from source for cross-batch)
  sourceBatchId?: string;  // Source batch ID (for audit trail)
  sourceSessionId: string;
  newSessionId: string;
  documentsCloned: number;
  documentPlaceholdersCreated?: number;  // Count of placeholder docs (cross-batch)
  isCrossBatch?: boolean;  // True if this was a cross-batch template clone
  clonedAt: string;
}

// Conflict check types
export interface BRRConflictExactMatch {
  exists: true;
  sessionId: string;
  userEmail: string;
  status: string;
  savedAt: string | null;
  duplicateCount?: number; // Number of reviews with same batch+lot (> 1 means duplicates exist)
}

export interface BRRConflictPartialMatch {
  sessionId: string;
  userEmail: string;
  lotKey: string;
  overlappingLots: string[];
  status: string;
}

export interface BRRConflictCheckResponse {
  exactMatch: BRRConflictExactMatch | null;
  partialMatches: BRRConflictPartialMatch[];
}

// Request payload for saving a specific page
export interface BRRReviewPayload {
  batchId: string;
  sessionId: string;
  pageName: 'start_page' | 'document_validation' | 'brr_form' | 'qp_review';
  pageData: StartPageData | DocumentValidationData | BRRFormData | QPReviewData | Record<string, unknown>;
  batchType?: 'direct' | 'indirect';
  priority?: Priority;
  isAdmin?: boolean;
  status?: 'draft' | 'submitted';
}

export interface BRRReviewSaveResponse {
  success: boolean;
  batch_id: number;
  session_id: string;
  warnings?: string[]; // Validation warnings from backend
}

// QAA Check Response
export interface QAACheckResponse {
  found: boolean;
  file_missing: boolean;
  matched_firma: string | null;
  confidence: number | null;
  signing_date: string | null;
  review_period: string | null;
  review_date: string | null;
  box_file_url: string;
}

// Box Folder Scan Types
export interface BoxFileEntry {
  id: string;
  name: string;
  type: 'file' | 'folder';
  modified_at: string;
  path: string;
  box_link: string;
}

export interface BoxFolderScanResult {
  current_snapshot: Record<string, BoxFileEntry>;
  changes: {
    new_files: BoxFileEntry[];
    modified_files: BoxFileEntry[];
    deleted_files: BoxFileEntry[];
  };
  timed_out: boolean;
  items_scanned: number;
}

export interface BoxFolderInfo {
  name: string;
  id: string;
  total_count: number;
}

export type BoxSubsectionKey = 'specsApi' | 'primaryPackagingMaterials' | 'manufacturingInstructions' | 'packagingInstructions' | 'releaseTestInstructions' | 'releaseSpecifications';

// Individual Box link entry with its own version/freeText
export interface BoxLinkEntry {
  id: string;
  link: string;
  folder_id: string;
  certified_at: string;
  snapshot: Record<string, BoxFileEntry>;
  version: string;
  freeText: string;
}

// New multi-link structure
export interface BoxSubsectionData {
  entries: BoxLinkEntry[];
}

// Legacy single-link structure (for backward compatibility during migration)
export interface LegacyBoxSubsectionData {
  link: string;
  folder_id: string;
  certified_at: string;
  snapshot: Record<string, BoxFileEntry>;
}

// Union type for loading data (could be old or new format)
export type BoxSubsectionDataRaw = BoxSubsectionData | LegacyBoxSubsectionData;

export type MaterialBoxFolders = Partial<Record<BoxSubsectionKey, BoxSubsectionDataRaw>>;

// Type guard to check if data is legacy format
export function isLegacyBoxData(data: BoxSubsectionDataRaw): data is LegacyBoxSubsectionData {
  return 'link' in data && !('entries' in data);
}

// Migrate legacy single-link data to new multi-link format
export function migrateBoxSubsectionData(
  data: BoxSubsectionDataRaw,
  version?: string,
  freeText?: string
): BoxSubsectionData {
  if (isLegacyBoxData(data)) {
    return {
      entries: [{
        id: crypto.randomUUID(),
        link: data.link,
        folder_id: data.folder_id,
        certified_at: data.certified_at,
        snapshot: data.snapshot,
        version: version || '',
        freeText: freeText || '',
      }],
    };
  }
  return data;
}

export const STANDARD_DOC_SECTIONS: Array<{ key: BoxSubsectionKey; title: string }> = [
  { key: 'specsApi', title: 'Specifications for Active Ingredients' },
  { key: 'primaryPackagingMaterials', title: 'Primary Packaging Specifications' },
  { key: 'manufacturingInstructions', title: 'Manufacturing Instructions' },
  { key: 'packagingInstructions', title: 'Packaging Instructions' },
  { key: 'releaseTestInstructions', title: 'Release Test Instructions' },
  { key: 'releaseSpecifications', title: 'Release Specifications' },
];

export const LEGACY_BOX_KEY_MAP: Record<string, BoxSubsectionKey> = {
  specs: 'specsApi',
  manufacturing: 'manufacturingInstructions',
  release: 'releaseTestInstructions',
};

export interface CustomDocumentSection {
  id: string;
  name: string;
  version: string;
  freeText: string;
  boxFolderUrl: string;
}

// Connection Status Types
export interface ServiceStatus {
  service: string;
  status: 'connected' | 'error';
  endpoint: string | null;
  latency_ms: number | null;
  details: Record<string, any>;
  error: string | null;
}

export interface ConnectionStatusResponse {
  timestamp: string;
  services: ServiceStatus[];
}

// API Configuration
export interface ApiConfig {
  baseUrl: string;
}

// Progress Tracking Types
export interface ProgressStep {
  name: string;           // Internal step identifier (e.g., 'getSavedReview')
  label: string;          // User-facing label (e.g., 'Loading saved review...')
  weight?: number;        // Optional weight for weighted progress calculation (default: 1)
}

export interface ProgressState {
  isActive: boolean;
  operationId: string;
  currentStep: number;
  totalSteps: number;
  currentStepLabel: string;
  percentage: number;
  estimatedTimeRemaining: number | null;  // In milliseconds, null if unknown
  title?: string;
}

export interface ProgressOverlayData {
  percentage: number;
  currentStepLabel: string;
  estimatedTimeRemaining: number | null;
  title?: string;
}
